import { useOrganizationPerms } from '@/organization/useOrganizationRole';
import { Add, Cancel, CheckCircle, Delete, Edit } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../departments/ConfirmDialog';
import { useOrg } from '../organization/useOrg';
import { FiscalYear } from '../types/fiscalYear';
import { FiscalYearEditModal } from './FiscalYearEditModal';
import {
  useDeleteFiscalYear,
  useFiscalYears,
  useSetActiveFiscalYear,
} from './useFiscalYears';

export function FiscalYearsPage() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { data: perms } = useOrganizationPerms(selectedOrg?.id);
  const canManageFiscalYrs = perms?.canManageFiscalYrs;
  const {
    data: fiscalYears,
    isLoading,
    error,
  } = useFiscalYears(selectedOrg?.id || '');
  const deleteMutation = useDeleteFiscalYear(selectedOrg?.id || '');
  const setActiveMutation = useSetActiveFiscalYear(selectedOrg?.id || '');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFiscalYear, setEditingFiscalYear] = useState<FiscalYear | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fiscalYearToDelete, setFiscalYearToDelete] =
    useState<FiscalYear | null>(null);

  const handleAdd = () => {
    setEditingFiscalYear(null);
    setEditModalOpen(true);
  };

  const handleEdit = (fiscalYear: FiscalYear) => {
    setEditingFiscalYear(fiscalYear);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (fiscalYear: FiscalYear) => {
    setFiscalYearToDelete(fiscalYear);
    setDeleteDialogOpen(true);
  };

  const navToProject = (fiscalYearId: string) => {
    navigate(`/tools/${fiscalYearId}`);
  };

  const handleConfirmDelete = async () => {
    if (fiscalYearToDelete) {
      try {
        await deleteMutation.mutateAsync(fiscalYearToDelete.id);
        setDeleteDialogOpen(false);
        // Clear the fiscal year after dialog animation completes
        setTimeout(() => {
          setFiscalYearToDelete(null);
        }, 300);
      } catch (error) {
        console.error('Failed to delete fiscal year:', error);
      }
    }
  };

  const handleSetActive = async (fiscalYear: FiscalYear) => {
    try {
      await setActiveMutation.mutateAsync(fiscalYear.id);
    } catch (error) {
      console.error('Failed to set fiscal year active state:', error);
    }
  };

  if (!selectedOrg) {
    return (
      <Container>
        <Box sx={{ mb: 3 }} />
        <Paper sx={{ p: 4 }}>
          <Typography variant='body1' color='text.primary' align='center'>
            No organization was selected, please join or select an organization
            to view fiscal years.
          </Typography>
        </Paper>
      </Container>
    );
  }
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error'>Error loading fiscal years</Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth='lg'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant='h4' component='h1'>
          Fiscal Years
        </Typography>
        {canManageFiscalYrs && (
          <Button variant='contained' startIcon={<Add />} onClick={handleAdd}>
            Add Fiscal Year
          </Button>
        )}
      </Box>

      {!fiscalYears?.length ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant='body1' color='text.secondary' align='center'>
            No fiscal years have been created yet.
            {canManageFiscalYrs && ' Click "Add Fiscal Year" to create one.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>View Projects</TableCell>
                {canManageFiscalYrs && (
                  <TableCell align='right'>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {fiscalYears.map((fiscalYear) => (
                <TableRow key={fiscalYear.id}>
                  <TableCell>{fiscalYear.name}</TableCell>
                  <TableCell>
                    {format(new Date(fiscalYear.startDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {format(new Date(fiscalYear.endDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {fiscalYear.isActive ? (
                      <Chip label='Active' color='success' size='small' />
                    ) : (
                      <Chip label='Inactive' size='small' />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant='contained'
                      onClick={() => navToProject(fiscalYear.id)}
                    >
                      Projects
                    </Button>
                  </TableCell>
                  {canManageFiscalYrs && (
                    <TableCell align='right'>
                      <Tooltip
                        title={
                          fiscalYear.isActive
                            ? 'Set as inactive'
                            : 'Set as active'
                        }
                      >
                        <span>
                          <IconButton
                            size='small'
                            onClick={() => handleSetActive(fiscalYear)}
                            disabled={setActiveMutation.isPending}
                            color={fiscalYear.isActive ? 'warning' : 'info'}
                          >
                            {fiscalYear.isActive ? <Cancel /> : <CheckCircle />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Edit'>
                        <IconButton
                          color={'info'}
                          size='small'
                          onClick={() => handleEdit(fiscalYear)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          fiscalYear.isActive
                            ? 'Cannot delete active fiscal year'
                            : 'Delete'
                        }
                      >
                        <span>
                          <IconButton
                            color={fiscalYear.isActive ? 'warning' : 'info'}
                            size='small'
                            onClick={() => handleDeleteClick(fiscalYear)}
                            disabled={fiscalYear.isActive}
                            sx={{
                              color: fiscalYear.isActive
                                ? 'warning.main'
                                : 'info.main',
                              '&.Mui-disabled': {
                                color: (theme) =>
                                  fiscalYear.isActive
                                    ? theme.palette.warning.main
                                    : theme.palette.info.main,
                                opacity: 0.5,
                              },
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <FiscalYearEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingFiscalYear(null);
        }}
        editingFiscalYear={editingFiscalYear}
        existingFiscalYears={fiscalYears}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onCancel={() => {
          setDeleteDialogOpen(false);
        }}
        onConfirm={handleConfirmDelete}
        title='Delete Fiscal Year'
        message={`Are you sure you want to delete the fiscal year "${fiscalYearToDelete?.name}"? This will delete all of its associated projects as well.`}
      />
    </Container>
  );
}
